<!-- components/ui/ImpersonationTransitionOverlay.vue -->
<!--
  Full-screen loading overlay for impersonation enter/exit. Mounted globally via
  GlobalOverlays so it is present on both the /admin (source) and dashboard
  (target) layouts and therefore survives the full-page reload each transition
  performs. See composables/useImpersonationTransition.ts.
-->
<script setup lang="ts">
import { onMounted } from 'vue'
import { Loader2 } from 'lucide-vue-next'
import { useImpersonationTransition } from '~/composables/useImpersonationTransition'

const { visible, message, resumeAfterReload, end } = useImpersonationTransition()

onMounted(() => {
  // If we just landed on the freshly reloaded page mid-transition, keep the
  // overlay up while the new identity's page paints its own loading state,
  // then release it so the user is never left staring at a frozen screen.
  if (resumeAfterReload()) {
    setTimeout(() => end(), 600)
  }
})
</script>

<template>
  <Teleport to="body">
    <Transition name="impersonation-fade">
      <div
        v-if="visible"
        class="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-4 px-6 text-center"
        style="background: rgba(15, 23, 42, 0.92)"
        role="status"
        aria-live="polite"
      >
        <Loader2 class="w-10 h-10 text-amber-400 animate-spin" :stroke-width="2" />
        <div class="text-sm font-medium text-white">{{ message }}</div>
        <div class="text-xs text-slate-400">Esto puede tardar unos segundos…</div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.impersonation-fade-enter-active,
.impersonation-fade-leave-active {
  transition: opacity 0.18s ease;
}
.impersonation-fade-enter-from,
.impersonation-fade-leave-to {
  opacity: 0;
}
</style>
